import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import { DateTime } from "luxon";
import "./FestivalCalendar.css";
import EventModal from "../pages/EventModal";

const FestivalCalendar = () => {
  const navigate = useNavigate();

  const [country, setCountry] = useState("US");
  const [events, setEvents] = useState([]);
  const [customEvents, setCustomEvents] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [hoveredFestival, setHoveredFestival] = useState(null);
  const [holidayType, setHolidayType] = useState("all");

  const today = new Date().toISOString().split("T")[0];
  const USER_ID = "11111111-1111-1111-1111-111111111111";

  // ---------------- FETCH HOLIDAYS ----------------
  useEffect(() => {
    fetch(`/api/holidays?country=${country}&year=2026&type=${holidayType}`)
      .then((res) => res.json())
      .then((data) => {
        if (!Array.isArray(data)) return setEvents([]);

        const mapped = data.map((h) => ({
          title: h.name,
          date: h.date,
          backgroundColor: "#6366f1",
          borderColor: "#4834d4",
          textColor: "#fff",
          extendedProps: {
            festival: h.name,
            type: h.type,
            country,
            description: getFestivalDescription(h.name),
          },
        }));
        setEvents(mapped);
      });
  }, [country, holidayType]);

  // ---------------- FETCH USER EVENTS ----------------
  const fetchUserEvents = () => {
    fetch(`/api/events?user_id=${USER_ID}`)
      .then((res) => res.json())
      .then((data) => {
        const draftsOnly = data.filter((e) => e.status === "draft");
        const published = data.filter((e) => e.status !== "draft");
        
        setDrafts(draftsOnly);

        const mappedDrafts = draftsOnly.map((e) => ({
          id: e.id,
          title: e.title,
          start: e.event_date,
          className: "event-draft",
          extendedProps: {
            status: "draft",
            description: e.description,
            notes: e.notes,
            country: e.country,
          },
        }));

        const mappedPublished = published.map((e) => {
          const isScheduled = e.status === "schedule";
          return {
            id: e.id,
            title: e.title,
            start: isScheduled ? e.scheduled_time : e.event_date,
            backgroundColor: isScheduled ? "#f39c12" : "#2ecc71",
            borderColor: "transparent",
            className: isScheduled ? "event-schedule" : "event-publish",
            extendedProps: {
              status: e.status,
              timezones: e.timezones || [],
              country: e.country,
              description: e.description,
              notes: e.notes,
            },
          };
        });

        setCustomEvents([...mappedDrafts, ...mappedPublished]);
      });
  };

  // ---------------- INITIAL LOAD ----------------
  useEffect(() => {
    fetchUserEvents();
  }, []);

  // ---------------- REFETCH ON TAB FOCUS ----------------
  useEffect(() => {
    const handleFocus = () => fetchUserEvents();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  // ---------------- HELPER ----------------
  const getFestivalDescription = (name) => {
    const descriptions = {
      Christmas: "🎄 Christmas celebrates the birth of Jesus Christ.",
      Diwali: "🪔 Festival of lights symbolizing victory of light over darkness.",
      Halloween: "🎃 A fun and spooky celebration with costumes and treats.",
    };
    return descriptions[name] || `✨ ${name} is a cultural celebration.`;
  };

  // ---------------- MODAL ----------------
  const handleDateClick = (info) => {
    setSelectedDate(info.dateStr);
    setShowModal(true);
  };

  const handleSaveEvent = (eventData) => {
    fetch("/api/events", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...eventData,
        created_by: USER_ID,
        scheduled_time: eventData.scheduled_time
          ? eventData.scheduled_time.replace("T", " ") + ":00"
          : null,
        notes: eventData.notes || "",
      }),
    })
      .then((res) => res.json())
      .then(() => {
        setShowModal(false);
        fetchUserEvents();
      });
  };

  // ---------------- PUBLISH ----------------
  const publishDraft = (id) => {
    fetch(`/api/events/${id}/publish`, { method: "PUT" })
      .then(() => fetchUserEvents())
      .catch((err) => console.error(err));
  };

  // ---------------- DELETE ----------------
  const deleteDraft = (id) => {
    fetch(`/api/events/${id}`, { method: "DELETE" }).then(() =>
      fetchUserEvents()
    );
  };

  // ---------------- HOVER FESTIVAL ----------------
  const handleEventMouseEnter = (info) => {
    const props = info.event.extendedProps;
    let timeInfo = "";

    // Multi-timezone support logic
    if (props.status === "schedule" && props.timezones?.length > 0) {
      const baseTime = DateTime.fromISO(info.event.startStr);
      timeInfo = props.timezones
        .map((tz) => {
          const converted = baseTime.setZone(tz);
          return `${converted.toFormat("h:mm a")} ${tz}`;
        })
        .join(" / ");
    }

    setHoveredFestival({
      name: info.event.title,
      date: info.event.startStr,
      country: props.country || country,
      description: props.description,
      notes: props.notes,
      status: props.status,
      timeInfo: timeInfo,
    });
  };

  const handleEventMouseLeave = () => {
    setHoveredFestival(null);
  };

  // ---------------- RENDER ----------------
  return (
    <div className="calendar-layout">
      {/* TOP CALENDAR */}
      <div className="calendar-card">
        <h2>Festival & Event Calendar</h2>

        <div className="calendar-filters">
          <select value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="US">United States</option>
            <option value="IN">India</option>
            <option value="GB">United Kingdom</option>
            <option value="CA">Canada</option>
          </select>

          <select
            value={holidayType}
            onChange={(e) => setHolidayType(e.target.value)}
          >
            <option value="all">All Holidays</option>
            <option value="public">Public</option>
            <option value="company">Company</option>
          </select>
        </div>

        <FullCalendar
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          events={[...events, ...customEvents]}
          height="100%"
          dateClick={handleDateClick}
          eventClick={(info) =>
            navigate(`/greeting-card/${encodeURIComponent(info.event.title)}`)
          }
          eventMouseEnter={handleEventMouseEnter}
          eventMouseLeave={handleEventMouseLeave}
          eventContent={(eventInfo) => {
            const status = eventInfo.event.extendedProps.status;
            return (
              <div className="calendar-event-content">
                {status === "draft" && (
                  <span className="calendar-event-status-icon">📎</span>
                )}
                {status === "schedule" && (
                  <span className="calendar-event-status-icon">⏰</span>
                )}
                <div className="fc-event-title">{eventInfo.event.title}</div>
              </div>
            );
          }}
          eventClassNames={(e) => {
            const classes = [];
            if (e.event.startStr < today) classes.push("past-event");
            if (e.event.extendedProps.status)
              classes.push(`event-${e.event.extendedProps.status}`);
            return classes;
          }}
        />
      </div>

      {/* BOTTOM SECTION */}
      <div className="bottom-section">
        {/* LEFT: HOVER FESTIVAL INFO */}
        <div className="modal-container">
          {hoveredFestival && (
            <div className="hover-festival-card">
              <h4>{hoveredFestival.name}</h4>
              <p>
                <b>Date:</b> {hoveredFestival.date}
              </p>
              <p>
                <b>Country:</b> {hoveredFestival.country}
              </p> <b>Description:</b> 
              <p>{hoveredFestival.description}</p>
              {hoveredFestival.timeInfo && (
                <p className="multi-tz-info">
                  <b>Times:</b> {hoveredFestival.timeInfo}
                </p>
              )}
              {hoveredFestival.notes && (
                <p>
                  <b>Notes:</b> {hoveredFestival.notes}
                </p>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: DRAFTS */}
        <div className="draft-panel">
          <h3>Your Drafts</h3>

          {drafts.length === 0 && <p>No drafts yet</p>}

          {drafts.map((d) => (
            <div key={d.id} className="draft-card">
              <h4>{d.title}</h4>
              <p>
                <b>Date:</b> {d.event_date || d.date}
              </p>
              <p>
                <b>Country:</b> {d.country}
              </p>
              <p>{d.description}</p>
              {d.notes && (
                <p>
                  <b>Notes:</b> {d.notes}
                </p>
              )}

              <button onClick={() => publishDraft(d.id)}>Publish</button>
              <button onClick={() => deleteDraft(d.id)}>Delete</button>
            </div>
          ))}
        </div>
      </div>

      {/* CREATE EVENT MODAL */}
      {showModal && (
        <EventModal
          date={selectedDate}
          onClose={() => setShowModal(false)}
          onSave={handleSaveEvent}
        />
      )}
    </div>
  );
};

export default FestivalCalendar;